import React from 'react';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormHelperText,
    InputAdornment,
    MenuItem,
    Pagination,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { useTranslation } from 'react-i18next';
import type {
    UploadedContract,
    UploadedContractErrors,
    SalaryBasisOption,
    CalcTypeOption,
    JobPositionUploadOption,
    ContractFieldDef,
} from '../../../hooks/useContracts';
import {
    CONTRACT_FIELDS,
    toDateInputValue,
    isCalcTypeValid,
    isSalaryBasisValid,
    isJobPositionValid,
    useUploadContractPreviewDialog,
} from '../../../hooks/useContracts';
import type { Employee } from '../../../types/employee';
import UploadContractPreviewMobile from '../mobile/UploadContractPreviewMobile';

interface UploadContractPreviewDialogProps {
    open: boolean;
    contracts: UploadedContract[];
    employees: Employee[];
    salaryBasisOptions: SalaryBasisOption[];
    salaryBasisLoading: boolean;
    calcTypeOptions: CalcTypeOption[];
    calcTypeLoading: boolean;
    onClose: () => void;
    onSave: () => void;
    onChange: (rowId: number, field: keyof UploadedContract, value: any) => void;
    saveLoading?: boolean;
    saveError?: { message: string; violations: string[] } | null;
}

const UploadContractPreviewDialog: React.FC<UploadContractPreviewDialogProps> = ({
    open,
    contracts,
    employees,
    salaryBasisOptions,
    salaryBasisLoading,
    calcTypeOptions,
    calcTypeLoading,
    onClose,
    onSave,
    onChange,
    saveLoading = false,
    saveError = null,
}) => {
    const { t } = useTranslation();
    const {
        isMobile,
        fullScreen,
        page,
        setPage,
        search,
        setSearch,
        showAllErrors,
        totalPages,
        contract,
        currentErrors,
        invalidCount,
        handleSaveClick,
        jobPositionOptions,
        jobPositionLoading,
        jobPositionLoaded,
    } = useUploadContractPreviewDialog({
        open,
        contracts,
        employees,
        calcTypeOptions,
        salaryBasisOptions,
        onChange,
        onSave,
    });

    const renderValue = (f: ContractFieldDef, row: UploadedContract) => {
        const fieldError = showAllErrors ? currentErrors[f.field as keyof UploadedContractErrors] : undefined;

        if (f.type === 'employee') {
            const empError = showAllErrors ? currentErrors.employeePersonalNo : undefined;
            const resolvedEmp = employees.find((e) => String(e.id) === row.resolvedEmployeeId);
            return (
                <FormControl fullWidth size="small" variant="standard" error={!!empError}>
                    <Select
                        value={row.resolvedEmployeeId}
                        displayEmpty
                        onChange={(e) => onChange(row._rowId, 'resolvedEmployeeId', e.target.value)}
                        renderValue={(val) =>
                            val
                                ? (() => {
                                    const e = employees.find((em) => String(em.id) === val);
                                    return e ? `${e.firstName} ${e.lastName}` : String(val);
                                })()
                                : <em style={{ color: '#aaa' }}>{t('employee.contract.upload.selectEmployee')}</em>
                        }
                    >
                        {employees.map((em) => (
                            <MenuItem key={String(em.id)} value={String(em.id)}>
                                {em.firstName} {em.lastName} ({em.personalNo})
                            </MenuItem>
                        ))}
                    </Select>
                    {!resolvedEmp && row.employeePersonalNo && (
                        <FormHelperText>
                            {empError ?? t('employee.contract.upload.personalNoNotFound', {personalNo: row.employeePersonalNo})}
                        </FormHelperText>
                    )}
                    {empError && resolvedEmp && (
                        <FormHelperText>{empError}</FormHelperText>
                    )}
                </FormControl>
            );
        }

        if (f.type === 'date') {
            return (
                <TextField
                    type="date"
                    value={toDateInputValue(row[f.field] as string)}
                    onChange={(e) => onChange(row._rowId, f.field, e.target.value)}
                    size="small"
                    variant="standard"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    error={!!fieldError}
                    helperText={fieldError}
                />
            );
        }

        if (f.type === 'calcType') {
            const valid = isCalcTypeValid(row.payrollCalcType, calcTypeOptions);
            const hasError = showAllErrors && !!currentErrors.payrollCalcType;
            return (
                <FormControl fullWidth size="small" variant="standard" error={hasError}>
                    <Select
                        value={valid ? row.payrollCalcType : ''}
                        displayEmpty
                        onChange={(e) => onChange(row._rowId, 'payrollCalcType', e.target.value)}
                        startAdornment={
                            calcTypeLoading ? (
                                <InputAdornment position="start">
                                    <CircularProgress size={14} />
                                </InputAdornment>
                            ) : null
                        }
                        renderValue={(val) =>
                            val
                                ? (calcTypeOptions.find((o) => o.id === val)?.name ?? String(val))
                                : <em style={{ color: '#aaa' }}>{t('employee.contract.upload.selectCalcType')}</em>
                        }
                    >
                        {calcTypeOptions.map((o) => (
                            <MenuItem key={o.id} value={o.id}>{o.name}</MenuItem>
                        ))}
                    </Select>
                    {hasError && <FormHelperText>{currentErrors.payrollCalcType}</FormHelperText>}
                </FormControl>
            );
        }

        if (f.type === 'withContribution') {
            const wcError = showAllErrors ? currentErrors.withContribution : undefined;
            return (
                <FormControl fullWidth size="small" variant="standard" error={!!wcError}>
                    <Select
                        value={row.withContribution}
                        displayEmpty
                        onChange={(e) => onChange(row._rowId, 'withContribution', e.target.value)}
                        renderValue={(val) =>
                            val === '1' ? t('common.actions.yes') : val === '0' ? t('common.actions.no') : <em style={{ color: '#aaa' }}>{t('employee.contract.upload.selectGeneric')}</em>
                        }
                    >
                        <MenuItem value="1">{t('common.actions.yes')}</MenuItem>
                        <MenuItem value="0">{t('common.actions.no')}</MenuItem>
                    </Select>
                    {wcError && <FormHelperText>{wcError}</FormHelperText>}
                </FormControl>
            );
        }

        if (f.type === 'salaryBasis') {
            const valid = isSalaryBasisValid(row.salaryBasis, salaryBasisOptions);
            const hasError = showAllErrors && !!currentErrors.salaryBasis;
            return (
                <FormControl fullWidth size="small" variant="standard" error={hasError}>
                    <Select
                        value={valid ? row.salaryBasis : ''}
                        displayEmpty
                        onChange={(e) => onChange(row._rowId, 'salaryBasis', e.target.value)}
                        startAdornment={
                            salaryBasisLoading ? (
                                <InputAdornment position="start">
                                    <CircularProgress size={14} />
                                </InputAdornment>
                            ) : null
                        }
                        renderValue={(val) =>
                            val
                                ? (salaryBasisOptions.find((o) => o.id === val)?.name ?? String(val))
                                : <em style={{ color: '#aaa' }}>{t('employee.contract.upload.selectSalaryBasis')}</em>
                        }
                    >
                        {salaryBasisOptions.map((o) => (
                            <MenuItem key={o.id} value={o.id}>{o.name}</MenuItem>
                        ))}
                    </Select>
                    {hasError && <FormHelperText>{currentErrors.salaryBasis}</FormHelperText>}
                </FormControl>
            );
        }

        if (f.type === 'jobPosition') {
            const resolvedEmp = employees.find((e) => String(e.id) === row.resolvedEmployeeId);
            const valid = isJobPositionValid(row.jobPosition, jobPositionOptions, jobPositionLoading);
            const displayJobName = row.jobPosition;
            const hasJobError = !valid && jobPositionLoaded && resolvedEmp !== undefined;
            const hasValidationError = showAllErrors && !!currentErrors.jobPosition;
            return (
                <FormControl fullWidth size="small" variant="standard" error={hasJobError || hasValidationError}>
                    <Select
                        value={valid ? row.jobPosition : ''}
                        displayEmpty
                        disabled={!resolvedEmp}
                        onChange={(e) => onChange(row._rowId, 'jobPosition', e.target.value)}
                        startAdornment={
                            jobPositionLoading ? (
                                <InputAdornment position="start">
                                    <CircularProgress size={14} />
                                </InputAdornment>
                            ) : null
                        }
                        renderValue={(val) =>
                            val
                                ? (jobPositionOptions.find((o) => o.id === val)?.name ?? String(val))
                                : <em style={{ color: '#aaa' }}>{resolvedEmp ? t('employee.contract.upload.selectJobPosition') : t('employee.contract.upload.selectEmployeeFirst')}</em>
                        }
                    >
                        {jobPositionOptions.map((o) => (
                            <MenuItem key={o.id} value={o.id}>{o.name}</MenuItem>
                        ))}
                    </Select>
                    {hasJobError && (
                        <FormHelperText>
                            {jobPositionOptions.length === 0
                                ? t('employee.contract.upload.noJobPositionsForBranch')
                                : t('employee.contract.upload.jobPositionNotFound', {jobPosition: displayJobName})}
                        </FormHelperText>
                    )}
                    {!hasJobError && hasValidationError && (
                        <FormHelperText>{currentErrors.jobPosition}</FormHelperText>
                    )}
                </FormControl>
            );
        }

        if (f.type === 'number') {
            return (
                <TextField
                    type="number"
                    size="small"
                    variant="standard"
                    fullWidth
                    value={row[f.field] as string}
                    onChange={(e) => onChange(row._rowId, f.field, e.target.value)}
                    error={!!fieldError}
                    helperText={fieldError}
                />
            );
        }

        // text (default)
        return (
            <TextField
                size="small"
                variant="standard"
                fullWidth
                value={row[f.field] as string}
                onChange={(e) => onChange(row._rowId, f.field, e.target.value)}
                error={!!fieldError}
                helperText={fieldError}
            />
        );
    };

    return (
        <Dialog
            open={open}
            onClose={saveLoading ? undefined : onClose}
            fullScreen={fullScreen}
            maxWidth="sm"
            fullWidth
            PaperProps={{ sx: { borderRadius: fullScreen ? 0 : 2 } }}
        >
            <DialogTitle sx={{ fontWeight: 700 }}>
                {t('employee.contract.upload.dialogTitle')}
                {totalPages > 0 && (
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                        {search.trim()
                            ? t('employee.upload.pageIndicatorFiltered', {page, totalPages, total: contracts.length})
                            : t('employee.upload.pageIndicator', {page, totalPages})}
                    </Typography>
                )}
            </DialogTitle>

            <DialogContent dividers sx={{ p: isMobile ? 2 : 3 }}>
                <TextField
                    fullWidth
                    size="small"
                    placeholder={t('employee.contract.upload.searchPlaceholder')}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    sx={{ mb: 2 }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" color="action" />
                            </InputAdornment>
                        ),
                        endAdornment: search ? (
                            <InputAdornment position="end">
                                <ClearIcon
                                    fontSize="small"
                                    sx={{ cursor: 'pointer', color: 'text.secondary' }}
                                    onClick={() => setSearch('')}
                                />
                            </InputAdornment>
                        ) : null,
                    }}
                />

                {saveError && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        <Typography variant="body2" fontWeight="bold">{saveError.message}</Typography>
                        {saveError.violations.length > 0 && (
                            <ul style={{ margin: '4px 0 0', paddingLeft: '1.25rem' }}>
                                {saveError.violations.map((v, i) => (
                                    <li key={i}>{v}</li>
                                ))}
                            </ul>
                        )}
                    </Alert>
                )}

                {showAllErrors && invalidCount > 0 && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        <Typography variant="body2" fontWeight="bold">
                            {t('employee.contract.upload.invalidCount', {count: invalidCount})}
                        </Typography>
                        <Typography variant="body2">
                            {t('employee.contract.upload.fixHighlighted')}
                        </Typography>
                    </Alert>
                )}

                {!contract ? (
                    <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4 }}>
                        {search.trim() ? t('employee.table.noResultsFor', {query: search}) : t('employee.contract.upload.noContractsToPreview')}
                    </Typography>
                ) : isMobile ? (
                    <UploadContractPreviewMobile
                        contracts={[contract]}
                        employees={employees}
                        salaryBasisOptions={salaryBasisOptions}
                        salaryBasisLoading={salaryBasisLoading}
                        calcTypeOptions={calcTypeOptions}
                        calcTypeLoading={calcTypeLoading}
                        jobPositionOptions={jobPositionOptions}
                        jobPositionLoading={jobPositionLoading}
                        jobPositionLoaded={jobPositionLoaded}
                        onChange={onChange}
                        errors={currentErrors}
                        showAllErrors={showAllErrors}
                    />
                ) : (
                    <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                            <TableBody>
                                {CONTRACT_FIELDS.map((f) => (
                                    <TableRow key={String(f.field)}>
                                        <TableCell sx={{ fontWeight: 'bold', width: '40%', whiteSpace: 'nowrap' }}>
                                            {t(f.labelKey)}
                                        </TableCell>
                                        <TableCell>{renderValue(f, contract)}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}

                {totalPages > 1 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                        <Pagination
                            color="primary"
                            count={totalPages}
                            page={page}
                            onChange={(_, value) => setPage(value)}
                        />
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
                <Button variant="outlined" color="inherit" startIcon={<CloseIcon />} onClick={onClose} disabled={saveLoading}>
                    {t('common.actions.close')}
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={saveLoading ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                    onClick={handleSaveClick}
                    disabled={saveLoading}
                >
                    {t('common.actions.save')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default UploadContractPreviewDialog;
